import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  TextField,
  Box,
  Paper,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../services/providers/authProvider";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import loginService from "../../services/loginService";
import { AccessGateContext } from "../../context/accessGateContext";
import Logo from "../../assets/image/logo.png";
import LoginBackground from "../Shared/loginBackground";
import { VENDOR_PASSWORD_RESET_PATH } from "../../config/vendorAuthPaths";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { loginWithToken } = useAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { allowVendorAreaAccess } = useContext(AccessGateContext);
  const [passwordResetNotice, setPasswordResetNotice] = useState("");

  useEffect(() => {
    if (location.state?.passwordResetOk) {
      setPasswordResetNotice("Password aggiornata. Effettua l'accesso.");
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleVendorNavigate = useCallback(() => {
    allowVendorAreaAccess();
    navigate("/dashboard", { replace: true });
  }, [allowVendorAreaAccess, navigate]);


  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !password) {
      setError("Compila tutti i campi richiesti.");
      return;
    }
    if (!emailRegex.test(email)) {
      setError("Inserisci un formato email valido.");
      return;
    }
    if (password.length < 6) {
      setError("La password deve avere almeno 6 caratteri.");
      return;
    }

    setLoading(true);
    const data = {
      email,
      password: btoa(password),
      type: "vendor",
    };

    try {
      const response = await loginService.login(data);
      const token = response.data.token;

      if (token) {
        try {
          const principal = loginWithToken(token);
          const userRole = principal?.role?.toLowerCase?.() ?? "";
          if (userRole === "vendor") {
            handleVendorNavigate();
          } else {
            window.location.reload();
          }
        } catch (loginErr) {
          if (loginErr?.message === "TOKEN_EXPIRED") {
            setError("Sessione scaduta.");
          } else {
            setError("Accesso non riuscito. Riprova.");
          }
        }
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setError(
        typeof backendMessage === "string" && backendMessage.trim()
          ? backendMessage
          : "Email o password errati. Riprova.",
      );
    } finally {
      setLoading(false);
    }
  };


  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <LoginBackground title="Wow - Accesso Vendor">
      <Paper
        sx={{
          p: { xs: 6, sm: 6, md: 6 },
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: { xs: "100%", sm: "450px", md: "450px" },
          height: { xs: "100%", sm: "610px", md: "610px" },
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: "2px 2px 18px 5px rgba(0, 0, 0, 0.6)",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "86%",
            height: "90%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 5,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, mb: 0 }}>
            <img
              src={Logo}
              alt="Logo Wow che prezzi"
              height="90"
              style={{
                objectFit: "contain",
                width: "auto",
                maxHeight: "110px",
                display: "block"
              }}
            />

            <Box sx={{ textAlign: "center", width: "100%" }}>
              <Typography
                variant="h4"
                sx={{ color: "#0b3d87", mb: 1, fontFamily: "Rubik", fontWeight: "bold" }}
              >
                Benvenuto!
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: "#0b3d87", fontFamily: "Rubik", fontWeight: "normal", }}>
                Accedi al pannello{" "}
                <Box component="span" sx={{ color: "#e00036", fontFamily: "Rubik", fontWeight: "bold", display: "inline" }}>
                  Vendor
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {passwordResetNotice ? (
              <Alert severity="success" sx={{ width: "100%", mb: 2, mt: 0 }} aria-live="polite">
                {passwordResetNotice}
              </Alert>
            ) : null}
            {error && (
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  mb: 2,
                  mt: 0,
                }}
                aria-live="assertive"
              >
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleLogin}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2,
                mt: 0,
                flexBasis: 0,
              }}
            >
              <TextField
                fullWidth
                id="email"
                label={<span style={{ color: "#0b3d87", fontStyle: "italic", fontFamily: "Rubik" }}>Email</span>}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-invalid={!!error}
                placeholder=""
                variant="filled"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    backgroundColor: "#edf3ff",
                    borderRadius: "8px",
                    fontStyle: "italic",
                  },
                }}
              />

              <TextField
                fullWidth
                id="password"
                label={<span style={{ color: "#0b3d87", fontStyle: "italic", fontFamily: "Rubik" }}>Password</span>}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                aria-invalid={!!error}
                placeholder=""
                variant="filled"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    backgroundColor: "#edf3ff",
                    borderRadius: "8px",
                    fontStyle: "italic",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        tabIndex={-1}
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <Link
                  component={RouterLink}
                  to={VENDOR_PASSWORD_RESET_PATH}
                  underline="hover"
                  sx={{ fontSize: 14, color: "#0b3d87", fontFamily: "Rubik" }}
                >
                  Hai dimenticato la password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.4,
                  fontFamily: "Rubik",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  borderRadius: "999px",
                  backgroundColor: "#0b3d87",
                  "&:hover": {
                    backgroundColor: "#062a63",
                  },
                  minHeight: 48,
                  // Uniform margin
                }}
              >
                {loading ? "Accesso in corso..." : "ACCEDI"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </LoginBackground>
  );
};

export default Login;
